import React, { useEffect, useState, useCallback, useRef } from 'react';
import { format, subDays } from 'date-fns';
import { DateRange } from 'react-date-range';
import { FiSearch, FiCheck, FiX, FiEye, FiRefreshCw, FiDownload, FiFilter, FiCalendar } from 'react-icons/fi';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import Pagination from '../components/common/Pagination';
import Modal from '../components/common/Modal';
import EmptyState from '../components/common/EmptyState';
import transactionService from '../services/transactionService';
import projectService from '../services/projectService';
import { toast } from 'react-toastify';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import './Transactions.css';

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'rejected', label: 'Rejected' }
];

const payoutTypeOptions = [
  { value: '', label: 'All Types' },
  { value: 'FIAT', label: 'FIAT' },
  { value: 'CRYPTO', label: 'CRYPTO' }
];

const fiatProviders = [
  { value: 'FIAT_REPUBLIC', label: 'Fiat Republic' },
  { value: 'BVNK', label: 'BVNK' },
  { value: 'FV_BANK', label: 'FV Bank' }
];

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    project_id: '',
    status: '',
    payout_type: '',
    search: '',
    start_date: '',
    end_date: ''
  });

  // Date range picker
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: 'selection'
    }
  ]);
  const datePickerRef = useRef(null);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDateRangeChange = (ranges) => {
    setDateRange([ranges.selection]);
    if (ranges.selection.startDate && ranges.selection.endDate) {
      setFilters(prev => ({
        ...prev,
        start_date: format(ranges.selection.startDate, 'yyyy-MM-dd'),
        end_date: format(ranges.selection.endDate, 'yyyy-MM-dd')
      }));
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  };

  const clearDateRange = () => {
    setDateRange([{ startDate: null, endDate: null, key: 'selection' }]);
    setFilters(prev => ({ ...prev, start_date: '', end_date: '' }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getDateRangeDisplay = () => {
    if (dateRange[0].startDate && dateRange[0].endDate) {
      return `${format(dateRange[0].startDate, 'MMM dd, yyyy')} - ${format(dateRange[0].endDate, 'MMM dd, yyyy')}`;
    }
    return 'Select Date Range';
  };

  // Modals
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Decline confirmation modal
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);

  // Approve modals
  const [showFiatProviderModal, setShowFiatProviderModal] = useState(false);
  const [showCryptoConfirmModal, setShowCryptoConfirmModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('');

  const fetchProjects = async () => {
    try {
      const response = await projectService.getProjects();
      setProjects(response.data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      };
      const response = await transactionService.getTransactions(params);
      setTransactions(response.data || []);
      setPagination(response.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
    } catch (error) {
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTransactions();
  };

  const handleRefresh = () => {
    fetchTransactions();
    setSelectedTransactions([]);
    toast.success('Transactions refreshed');
  };

  const handleExport = async () => {
    try {
      await transactionService.exportTransactions(filters);
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export transactions');
    }
  };

  const handleSelectTransaction = (id) => {
    setSelectedTransactions(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const pendingIds = transactions.filter(t => t.status === 'pending').map(t => t.id);
    if (selectedTransactions.length === pendingIds.length) {
      setSelectedTransactions([]);
    } else {
      setSelectedTransactions(pendingIds);
    }
  };

  // Open approve modal based on payout type
  const handleApproveClick = (transaction) => {
    setSelectedTransaction(transaction);
    if (transaction.payout_type === 'CRYPTO') {
      setShowCryptoConfirmModal(true);
    } else {
      setShowFiatProviderModal(true);
    }
  };

  // Confirm FIAT approval with provider
  const handleFiatApprove = async () => {
    if (!selectedProvider) {
      toast.error('Please select a payout provider');
      return;
    }
    setActionLoading(true);
    try {
      await transactionService.approveTransaction(selectedTransaction.id, selectedProvider);
      toast.success('Transaction approved');
      setShowFiatProviderModal(false);
      setSelectedProvider('');
      setShowDetails(false);
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve transaction');
    } finally {
      setActionLoading(false);
    }
  };

  // Confirm CRYPTO approval
  const handleCryptoApprove = async () => {
    setActionLoading(true);
    try {
      await transactionService.approveTransaction(selectedTransaction.id, 'BINANCE');
      toast.success('Transaction approved');
      setShowCryptoConfirmModal(false);
      setShowDetails(false);
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve transaction');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedTransactions.length === 0) return;
    setActionLoading(true);
    try {
      const result = await transactionService.bulkApproveTransactions(selectedTransactions);
      toast.success(`Approved ${result.data.success.length} transactions`);
      if (result.data.failed.length > 0) {
        toast.warning(`${result.data.failed.length} transactions failed`);
      }
      setSelectedTransactions([]);
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to approve transactions');
    } finally {
      setActionLoading(false);
    }
  };

  // Open decline confirmation
  const handleDeclineClick = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDeclineConfirm(true);
  };

  // Proceed to reject reason modal after confirmation
  const handleDeclineConfirm = () => {
    setShowDeclineConfirm(false);
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setActionLoading(true);
    try {
      await transactionService.rejectTransaction(selectedTransaction.id, rejectReason);
      toast.success('Transaction rejected');
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedTransaction(null);
      setShowDetails(false);
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject transaction');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    if (selectedTransactions.length === 0) return;
    setActionLoading(true);
    try {
      const result = await transactionService.bulkRejectTransactions(selectedTransactions, rejectReason);
      toast.success(`Rejected ${result.data.success.length} transactions`);
      setShowBulkRejectModal(false);
      setRejectReason('');
      setSelectedTransactions([]);
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to reject transactions');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'info';
      case 'processing': return 'info';
      case 'completed': return 'success';
      case 'failed': return 'danger';
      case 'rejected': return 'danger';
      default: return 'default';
    }
  };

  const projectOptions = [
    { value: '', label: 'All Projects' },
    ...projects.map(p => ({ value: p.id, label: p.display_name }))
  ];

  const pendingCount = transactions.filter(t => t.status === 'pending').length;

  return (
    <Layout title="Transactions">
      <div className="transactions-page">
        <Card
          title="Transactions"
          subtitle="Manage payout transaction requests"
          actions={
            <div className="header-actions">
              <Button variant="secondary" onClick={() => setShowFilters(!showFilters)}>
                <FiFilter /> Filters
              </Button>
              <Button variant="secondary" onClick={handleExport}>
                <FiDownload /> Export
              </Button>
              <Button variant="secondary" onClick={handleRefresh}>
                <FiRefreshCw />
              </Button>
            </div>
          }
        >
          {/* Filters */}
          {showFilters && (
            <div className="filters-section">
              <div className="filters-grid">
                <Select
                  label="Project"
                  value={filters.project_id}
                  onChange={(e) => handleFilterChange('project_id', e.target.value)}
                  options={projectOptions}
                />
                <Select
                  label="Status"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  options={statusOptions}
                />
                <Select
                  label="Payout Type"
                  value={filters.payout_type}
                  onChange={(e) => handleFilterChange('payout_type', e.target.value)}
                  options={payoutTypeOptions}
                />
                <div className="date-range-wrapper" ref={datePickerRef}>
                  <label className="input-label">Date Range</label>
                  <div className="date-range-input" onClick={() => setShowDatePicker(!showDatePicker)}>
                    <FiCalendar className="date-icon" />
                    <span className={dateRange[0].startDate ? 'date-value' : 'date-placeholder'}>
                      {getDateRangeDisplay()}
                    </span>
                    {dateRange[0].startDate && (
                      <button
                        type="button"
                        className="clear-date-btn"
                        onClick={(e) => { e.stopPropagation(); clearDateRange(); }}
                      >
                        <FiX />
                      </button>
                    )}
                  </div>
                  {showDatePicker && (
                    <div className="date-range-dropdown">
                      <DateRange
                        ranges={dateRange}
                        onChange={handleDateRangeChange}
                        moveRangeOnFirstSelection={false}
                        months={1}
                        direction="horizontal"
                        rangeColors={['#6366f1']}
                        maxDate={new Date()}
                      />
                    </div>
                  )}
                </div>
              </div>
              <form onSubmit={handleSearch} className="search-form">
                <Input
                  placeholder="Search by reference ID, name, or account..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  icon={<FiSearch />}
                />
                <Button type="submit">Search</Button>
              </form>
            </div>
          )}

          {/* Bulk Actions */}
          {selectedTransactions.length > 0 && (
            <div className="bulk-actions">
              <span className="selected-count">{selectedTransactions.length} selected</span>
              <Button size="small" onClick={handleBulkApprove} loading={actionLoading}>
                <FiCheck /> Approve Selected
              </Button>
              <Button size="small" variant="danger" onClick={() => setShowBulkRejectModal(true)}>
                <FiX /> Reject Selected
              </Button>
              <Button size="small" variant="secondary" onClick={() => setSelectedTransactions([])}>
                Clear Selection
              </Button>
            </div>
          )}

          {/* Table */}
          <div className="table-container">
            {loading ? (
              <div className="loading-state">Loading...</div>
            ) : transactions.length === 0 ? (
              <EmptyState
                title="No transactions found"
                description="Transactions will appear here when projects submit payout requests"
              />
            ) : (
              <table className="transactions-table">
                <thead>
                  <tr>
                    {pendingCount > 0 && (
                      <th className="checkbox-col">
                        <input
                          type="checkbox"
                          checked={selectedTransactions.length === pendingCount && pendingCount > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                    )}
                    <th>Reference</th>
                    <th>Project</th>
                    <th>Amount</th>
                    <th>Beneficiary</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr key={tx.id}>
                      {pendingCount > 0 && (
                        <td className="checkbox-col">
                          {tx.status === 'pending' && (
                            <input
                              type="checkbox"
                              checked={selectedTransactions.includes(tx.id)}
                              onChange={() => handleSelectTransaction(tx.id)}
                            />
                          )}
                        </td>
                      )}
                      <td>
                        <div className="reference-cell">
                          <span className="reference-id">{tx.reference_id}</span>
                          <span className="payment-method">
                            {tx.payout_type === 'CRYPTO'
                              ? `${tx.token_symbol} (${tx.network})`
                              : tx.payment_method || 'FIAT'}
                          </span>
                        </div>
                      </td>
                      <td>{tx.project?.display_name || '-'}</td>
                      <td>
                        <div className="amount-cell">
                          <span className="amount">{parseFloat(tx.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                          <span className="currency">{tx.currency}</span>
                        </div>
                      </td>
                      <td>
                        <div className="beneficiary-cell">
                          <span className="beneficiary-name">{tx.beneficiary_name}</span>
                          <span className="beneficiary-account">
                            {tx.payout_type === 'CRYPTO' && tx.wallet_address
                              ? `${tx.wallet_address.substring(0, 8)}...${tx.wallet_address.substring(tx.wallet_address.length - 6)}`
                              : tx.beneficiary_account_number}
                          </span>
                        </div>
                      </td>
                      <td>
                        <Badge variant={getStatusVariant(tx.status)}>{tx.status}</Badge>
                      </td>
                      <td>{format(new Date(tx.created_at), 'MMM dd, yyyy HH:mm')}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-btn"
                            onClick={() => { setSelectedTransaction(tx); setShowDetails(true); }}
                            title="View Details"
                          >
                            <FiEye />
                          </button>
                          {tx.status === 'pending' && (
                            <>
                              <button
                                className="action-btn success"
                                onClick={() => handleApproveClick(tx)}
                                title="Approve"
                              >
                                <FiCheck />
                              </button>
                              <button
                                className="action-btn danger"
                                onClick={() => handleDeclineClick(tx)}
                                title="Reject"
                              >
                                <FiX />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {pagination.pages > 1 && (
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.pages}
              onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
            />
          )}
        </Card>
      </div>

      {/* Transaction Details Modal */}
      <Modal
        isOpen={showDetails}
        onClose={() => { setShowDetails(false); setSelectedTransaction(null); }}
        title="Transaction Details"
        size="large"
      >
        {selectedTransaction && (
          <div className="transaction-details">
            <div className="detail-section">
              <h4>Transaction Info</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Reference ID</label>
                  <span>{selectedTransaction.reference_id}</span>
                </div>
                <div className="detail-item">
                  <label>Project</label>
                  <span>{selectedTransaction.project?.display_name}</span>
                </div>
                <div className="detail-item">
                  <label>Amount</label>
                  <span>{parseFloat(selectedTransaction.amount).toLocaleString()} {selectedTransaction.currency}</span>
                </div>
                <div className="detail-item">
                  <label>Payment Method</label>
                  <span>{selectedTransaction.payment_method || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Payout Type</label>
                  <Badge variant={selectedTransaction.payout_type === 'CRYPTO' ? 'info' : 'default'}>
                    {selectedTransaction.payout_type || 'FIAT'}
                  </Badge>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  <Badge variant={getStatusVariant(selectedTransaction.status)}>{selectedTransaction.status}</Badge>
                </div>
                <div className="detail-item">
                  <label>Purpose</label>
                  <span>{selectedTransaction.purpose || '-'}</span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Remitter Details ({selectedTransaction.remitter_type})</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Name</label>
                  <span>{selectedTransaction.remitter_name}</span>
                </div>
                <div className="detail-item">
                  <label>Email</label>
                  <span>{selectedTransaction.remitter_email || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Phone</label>
                  <span>{selectedTransaction.remitter_phone || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Country</label>
                  <span>{selectedTransaction.remitter_country || '-'}</span>
                </div>
              </div>
            </div>

            {selectedTransaction.payout_type === 'CRYPTO' ? (
              <div className="detail-section">
                <h4>Crypto Details</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Beneficiary Name</label>
                    <span>{selectedTransaction.beneficiary_name}</span>
                  </div>
                  <div className="detail-item">
                    <label>Wallet Address</label>
                    <span className="mono">{selectedTransaction.wallet_address}</span>
                  </div>
                  <div className="detail-item">
                    <label>Network</label>
                    <span>{selectedTransaction.network}</span>
                  </div>
                  <div className="detail-item">
                    <label>Token</label>
                    <span>{selectedTransaction.token_symbol}</span>
                  </div>
                  <div className="detail-item">
                    <label>Transaction Hash</label>
                    <span className="mono">{selectedTransaction.transaction_hash || 'Pending'}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="detail-section">
                <h4>Beneficiary Details ({selectedTransaction.beneficiary_type})</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Name</label>
                    <span>{selectedTransaction.beneficiary_name}</span>
                  </div>
                  <div className="detail-item">
                    <label>Account Number</label>
                    <span>{selectedTransaction.beneficiary_account_number}</span>
                  </div>
                  <div className="detail-item">
                    <label>Bank Name</label>
                    <span>{selectedTransaction.beneficiary_bank_name}</span>
                  </div>
                  <div className="detail-item">
                    <label>Bank Code (SWIFT/BIC)</label>
                    <span>{selectedTransaction.beneficiary_bank_code || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>IBAN</label>
                    <span>{selectedTransaction.beneficiary_iban || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Routing Number</label>
                    <span>{selectedTransaction.beneficiary_routing_number || '-'}</span>
                  </div>
                </div>
              </div>
            )}

            {selectedTransaction.status === 'pending' && (
              <div className="modal-actions">
                <Button variant="secondary" onClick={() => setShowDetails(false)}>
                  Close
                </Button>
                <Button
                  variant="danger"
                  onClick={() => { setShowDetails(false); handleDeclineClick(selectedTransaction); }}
                >
                  <FiX /> Reject
                </Button>
                <Button onClick={() => { setShowDetails(false); handleApproveClick(selectedTransaction); }}>
                  <FiCheck /> Approve
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => { setShowRejectModal(false); setRejectReason(''); }}
        title="Reject Transaction"
      >
        <div className="reject-modal">
          <p>Please provide a reason for rejecting this transaction:</p>
          <Input
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter rejection reason..."
            multiline
          />
          <div className="modal-actions">
            <Button variant="secondary" onClick={() => { setShowRejectModal(false); setRejectReason(''); }}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleReject} loading={actionLoading}>
              Reject Transaction
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Reject Modal */}
      <Modal
        isOpen={showBulkRejectModal}
        onClose={() => { setShowBulkRejectModal(false); setRejectReason(''); }}
        title="Reject Selected Transactions"
      >
        <div className="reject-modal">
          <p>You are about to reject {selectedTransactions.length} transactions. Please provide a reason:</p>
          <Input
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter rejection reason..."
            multiline
          />
          <div className="modal-actions">
            <Button variant="secondary" onClick={() => { setShowBulkRejectModal(false); setRejectReason(''); }}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleBulkReject} loading={actionLoading}>
              Reject {selectedTransactions.length} Transactions
            </Button>
          </div>
        </div>
      </Modal>

      {/* Decline Confirmation Modal */}
      <Modal
        isOpen={showDeclineConfirm}
        onClose={() => setShowDeclineConfirm(false)}
        title="Confirm Decline"
      >
        <div className="confirm-modal">
          <p>Are you sure you want to decline this transaction?</p>
          {selectedTransaction && (
            <div className="confirm-details">
              <p><strong>Reference:</strong> {selectedTransaction.reference_id}</p>
              <p><strong>Amount:</strong> {parseFloat(selectedTransaction.amount).toLocaleString()} {selectedTransaction.currency}</p>
              <p><strong>Beneficiary:</strong> {selectedTransaction.beneficiary_name}</p>
            </div>
          )}
          <div className="modal-actions">
            <Button variant="secondary" onClick={() => setShowDeclineConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeclineConfirm}>
              Yes, Decline
            </Button>
          </div>
        </div>
      </Modal>

      {/* FIAT Provider Selection Modal */}
      <Modal
        isOpen={showFiatProviderModal}
        onClose={() => { setShowFiatProviderModal(false); setSelectedProvider(''); }}
        title="Select Payout Provider"
      >
        <div className="provider-modal">
          <p>Select a third-party provider to process this FIAT payout:</p>
          {selectedTransaction && (
            <div className="confirm-details">
              <p><strong>Reference:</strong> {selectedTransaction.reference_id}</p>
              <p><strong>Amount:</strong> {parseFloat(selectedTransaction.amount).toLocaleString()} {selectedTransaction.currency}</p>
              <p><strong>Beneficiary:</strong> {selectedTransaction.beneficiary_name}</p>
              <p><strong>Bank:</strong> {selectedTransaction.beneficiary_bank_name}</p>
            </div>
          )}
          <div className="provider-options">
            {fiatProviders.map((provider) => (
              <label key={provider.value} className={`provider-option ${selectedProvider === provider.value ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="provider"
                  value={provider.value}
                  checked={selectedProvider === provider.value}
                  onChange={(e) => setSelectedProvider(e.target.value)}
                />
                <span className="provider-label">{provider.label}</span>
              </label>
            ))}
          </div>
          <div className="modal-actions">
            <Button variant="secondary" onClick={() => { setShowFiatProviderModal(false); setSelectedProvider(''); }}>
              Cancel
            </Button>
            <Button onClick={handleFiatApprove} loading={actionLoading} disabled={!selectedProvider}>
              <FiCheck /> Approve with {selectedProvider ? fiatProviders.find(p => p.value === selectedProvider)?.label : 'Provider'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* CRYPTO Confirmation Modal */}
      <Modal
        isOpen={showCryptoConfirmModal}
        onClose={() => setShowCryptoConfirmModal(false)}
        title="Confirm Crypto Payout"
      >
        <div className="confirm-modal">
          <p>Are you sure you want to approve this crypto payout?</p>
          {selectedTransaction && (
            <div className="confirm-details">
              <p><strong>Reference:</strong> {selectedTransaction.reference_id}</p>
              <p><strong>Amount:</strong> {parseFloat(selectedTransaction.amount).toLocaleString()} {selectedTransaction.currency}</p>
              <p><strong>Network:</strong> {selectedTransaction.network}</p>
              <p><strong>Token:</strong> {selectedTransaction.token_symbol}</p>
              <p><strong>Wallet:</strong> <span className="mono">{selectedTransaction.wallet_address}</span></p>
            </div>
          )}
          <p className="provider-note">This payout will be processed via <strong>Binance</strong>.</p>
          <div className="modal-actions">
            <Button variant="secondary" onClick={() => setShowCryptoConfirmModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCryptoApprove} loading={actionLoading}>
              <FiCheck /> Approve Crypto Payout
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default Transactions;
